import { cn } from '@/utils';
import { ChevronUp, CircleCheckBig, FileText, GraduationCap, NotebookPen, Star, Timer } from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { StudentAvatar } from '@/components/shared/StudentAvatar/StudentAvatar';
import { Button } from '@/components/ui/Button/Button';
import { Spinner } from '@/components/ui/Spinner/Spinner';
import { paths } from '@/config/paths';
import { useTimeFor } from '@/hooks/useTimeFor';
import { captureAnalyticsEvent } from '@/lib/mixpanel';
import { useAuthStore } from '@/stores/authStore';
import type { Student } from '@/types/student';
import { useGetAttendanceSessionSummary } from '../../api/getAttendanceSessionSummary';
import type { LastSession, SessionSummary } from '../../api/getLastAppointment';

interface ValueRowProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

const ValueRow = ({ icon, label, value }: ValueRowProps) => {
  return (
    <div className='flex w-full items-center justify-between'>
      <div className='desktop:flex-4 flex items-center gap-2'>
        {icon}
        <p>{label}</p>
      </div>
      <div className='desktop:flex-7'>
        <p className='text-body-m-bold'>{value}</p>
      </div>
    </div>
  );
};

interface SessionsHistoryItemProps {
  session: LastSession;
  sessionSummaryData?: SessionSummary;
  isAlwaysExpanded?: boolean;
  hideStudentInfo?: boolean;
  canFetchSessionSummary?: boolean;
  className?: string;
}

export const SessionsHistoryItem = ({
  session,
  sessionSummaryData,
  isAlwaysExpanded = false,
  hideStudentInfo = false,
  canFetchSessionSummary = false,
  className,
}: SessionsHistoryItemProps) => {
  const { t } = useTranslation('components', { keyPrefix: 'sessionsHistoryItem' });
  const { t: tScheduling } = useTranslation('scheduling');

  const { userData, studentsBgColors } = useAuthStore();
  const { format } = useTimeFor('sessionTime');

  const [isExpanded, setIsExpanded] = useState(false);

  const panelId = useId();

  const { data: fetchedSessionSummary, isLoading: isLoadingSessionSummary } = useGetAttendanceSessionSummary({
    attendanceId: session.attendanceId!,
    options: {
      enabled: !!session.attendanceId && canFetchSessionSummary && isExpanded && !sessionSummaryData && !session.sessionSummary,
    },
  });

  // Prefer prop, then session.sessionSummary from the normalized response, then a lazy fetch
  const rawSessionSummaryData = sessionSummaryData ?? session.sessionSummary ?? fetchedSessionSummary?.sessionSummary;
  // An id of 0 means the DWP session summary hasn't been sent from Radius yet — treat it as absent
  const currentSessionSummaryData = rawSessionSummaryData?.id ? rawSessionSummaryData : undefined;

  const sessionType: string | undefined = session.sessionType === 'private' ? tScheduling(`sessionType.${session.sessionType}`) : undefined;

  const shouldDisplayCounts =
    !!currentSessionSummaryData &&
    [
      currentSessionSummaryData.numberOfSkillsMasteredAndCompleted,
      currentSessionSummaryData.numberOfPagesCompleted,
      currentSessionSummaryData.numberOfSkillsInProgress,
      currentSessionSummaryData.numberOfStarsEarned,
    ].some((count) => count !== undefined && count !== null);

  const student = userData?.students.find((s: Student) => s.id === session.studentId);

  const hasSummaryAvailable = !!sessionSummaryData || !!session.sessionSummary || canFetchSessionSummary;
  const shouldShowExpandButton = (!isAlwaysExpanded && !!(hasSummaryAvailable && session.attendanceId)) || session.status;

  const hasFetchedOrFetchingData = isLoadingSessionSummary || !!currentSessionSummaryData;
  const shouldShowExpandedBody = ((hasFetchedOrFetchingData || (canFetchSessionSummary && isExpanded)) && (isAlwaysExpanded || isExpanded)) || isAlwaysExpanded;

  // A dropped-in session has no appointment ID but may have an attendance ID
  const isDroppedIn = session.appointmentId === null || session.appointmentId === undefined;
  const isFinalized = session.finalized == true;

  return (
    <div className={cn('bg-fill-background-light flex flex-col gap-6 rounded-lg p-6', className)}>
      <div className='flex items-center justify-between gap-3'>
        <div className='flex flex-1 flex-col gap-4'>
          {!hideStudentInfo && (
            <div className='flex items-center gap-2.5'>
              <StudentAvatar
                student={{
                  firstName: student?.firstName ?? 'Unknown',
                  lastName: student?.lastName ?? 'Unknown',
                  bgColor: student ? (studentsBgColors.get(student.id) ?? '') : '',
                }}
                className='size-7'
              />
              <p>{student?.firstName ?? 'Unknown'}</p>
            </div>
          )}
          <div className='flex flex-col gap-1'>
            <time dateTime={new Date(session.appointmentDate).toISOString()}>
              <p
                className={cn('text-body-m-semibold', {
                  'text-heading-s-bold': hideStudentInfo,
                })}
              >
                {format(session.appointmentDate, 'MMMM do, h:mm a')}
              </p>
            </time>
            <p className='text-text-default-secondary'>
              {!isDroppedIn && t('duration', { duration: session.durationMinutes })}
              {isDroppedIn && isFinalized && t('duration', { duration: session.durationMinutes })}
              {sessionType ? ` • ${sessionType}` : ''}
            </p>
          </div>
        </div>
        {shouldShowExpandButton && (
          <Button
            aria-controls={panelId}
            variant='icon'
            size='icon-l'
            onClick={() => {
              if (!isExpanded) {
                void captureAnalyticsEvent('sessionhistory_expanded', { appointment_id: session.appointmentId ?? session.attendanceId });
              }
              setIsExpanded((prev) => !prev);
            }}
            className='flex items-center justify-center px-2.5'
            aria-label={t(isExpanded ? 'expandButton.close' : 'expandButton.open')}
          >
            <ChevronUp className={cn('size-6', { 'rotate-180': !isExpanded })} aria-hidden />
          </Button>
        )}
      </div>
      {shouldShowExpandedBody && (
        <>
          <div className='flex flex-col gap-3' id={panelId} aria-live='polite'>
            {isLoadingSessionSummary ? (
              <div className='flex items-center justify-center p-4'>
                <Spinner className='size-10' />
              </div>
            ) : (
              <>
                {session.status && (
                  <div className='bg-fill-background-medium rounded-surface p-4'>
                    <ValueRow
                      icon={<GraduationCap className='text-text-default-secondary size-5' />}
                      label={t('sessionStatus')}
                      value={tScheduling(`appointmentStatus.${session.status}`)}
                    />
                  </div>
                )}
                {currentSessionSummaryData ? (
                  <>
                    {shouldDisplayCounts && (
                      <div className='bg-fill-background-medium rounded-surface flex flex-col items-center gap-4 p-4'>
                        {currentSessionSummaryData.numberOfSkillsMasteredAndCompleted !== undefined &&
                          currentSessionSummaryData.numberOfSkillsMasteredAndCompleted !== null && (
                            <ValueRow
                              icon={<CircleCheckBig className='text-text-default-secondary size-5' />}
                              label={t('masteredAndCompletedCount')}
                              value={currentSessionSummaryData.numberOfSkillsMasteredAndCompleted?.toString()}
                            />
                          )}
                        {currentSessionSummaryData.numberOfPagesCompleted !== undefined && currentSessionSummaryData.numberOfPagesCompleted !== null && (
                          <ValueRow
                            icon={<FileText className='text-text-default-secondary size-5' />}
                            label={t('pagesCompletedCount')}
                            value={currentSessionSummaryData.numberOfPagesCompleted ? currentSessionSummaryData.numberOfPagesCompleted?.toString() : '0'}
                          />
                        )}
                        {currentSessionSummaryData.numberOfSkillsInProgress !== undefined && currentSessionSummaryData.numberOfSkillsInProgress !== null && (
                          <ValueRow
                            icon={<Timer className='text-text-default-secondary size-5' />}
                            label={t('skillsInProgressCount')}
                            value={currentSessionSummaryData.numberOfSkillsInProgress ? currentSessionSummaryData.numberOfSkillsInProgress?.toString() : '0'}
                          />
                        )}
                        {currentSessionSummaryData.numberOfStarsEarned > 0 && (
                          <ValueRow
                            icon={<Star className='text-text-default-secondary size-5' />}
                            label={t('starsEarned')}
                            value={currentSessionSummaryData.numberOfStarsEarned.toString()}
                          />
                        )}
                      </div>
                    )}
                    {currentSessionSummaryData.didWorkOnAndCheckSchoolwork !== undefined && currentSessionSummaryData.didWorkOnAndCheckSchoolwork !== null && (
                      <div className='bg-fill-background-medium rounded-surface p-4'>
                        <ValueRow
                          icon={<NotebookPen className='text-text-default-secondary size-5' />}
                          label={t('didWorkOnSchoolwork')}
                          value={currentSessionSummaryData.didWorkOnAndCheckSchoolwork ? t('yes') : t('no')}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  canFetchSessionSummary && (
                    <div className='bg-fill-background-medium rounded-surface p-4'>
                      <p className='text-text-default-secondary'>{t('noSessionSummary', 'No session summary available for this appointment.')}</p>
                    </div>
                  )
                )}
              </>
            )}
          </div>
          {currentSessionSummaryData?.id && (
            <Button className='desktop:self-start' asChild>
              <Link className='flex items-center gap-2' to={paths.progressCenter.reports.sessionSummary.getHref(currentSessionSummaryData.id)}>
                <FileText />
                <span>{t('sessionSummaryButton')}</span>
              </Link>
            </Button>
          )}
        </>
      )}
    </div>
  );
};
